import { useMemo, useState } from 'react'
import { Head, router } from '@inertiajs/react'
import AppLayout from '@/components/AppLayout.tsx'
import PlanHeader from '../components/PlanHeader'
import PlanBreakdownSections, { type ChannelSectionData, type QuarterlySectionData } from './components/PlanBreakdownSections'
import ReviewTaskCard from './components/ReviewTaskCard'
import ReviewersCard, { type RevisionSubmit } from './components/ReviewersCard'
import RevisionBanner from './components/RevisionBanner'
import SubmitMessageCard from './components/SubmitMessageCard'
import { type RevisionBaseline } from './components/DiffBits'
import { type Person, type Task } from '@/pages/Tasks/task'
import { type PlanNodeFromServer } from './components/VersionViewOverallSection'
import { plan_index_path, plan_top_down_path, submissions_path } from '@/lib/routes'
import { useMarket } from '@/lib/useMarket'
import { revisionLabels } from './components/periodLabel'

interface Props {
  slug: string
  begin_date: string
  end_date: string
  top_down_plan: {
    id: string
    name: string
    slug: string
    revenue_target: number
    currency: string | null
    org_node: { id: string; name: string; slug: string } | null
  }
  // The comparison window the past-revenue figures come from.
  period: { start: string; end: string }
  // The plan period a revision's numbers cover (target months only); the
  // months before it are actual sales. Present for every version, but only
  // meaningful (narrower than begin_date–end_date) for a revision.
  plan_period: { start: string; end: string }
  // Number of months at the start of the plan already covered by actual
  // sales. Null for a plain version.
  actuals_months: number | null
  plan_nodes: PlanNodeFromServer[]
  channel_section: ChannelSectionData
  quarterly_section: QuarterlySectionData
  // The working copy against the published version; first_publish when nothing
  // is published yet. Null for a quarterly revision: it covers different months
  // than the published version, so a cell-by-cell diff would mislead.
  revision_diff: RevisionBaseline | null
  // The open cycle task (a plan's first submit): its card carries the reviewers.
  submit_task: Task | null
  people: Person[]
  // A revision of a published plan has no task yet: reviewers are picked here.
  revision_submit: RevisionSubmit | null
}

// The doer's submit page, GitHub's "Open a pull request": the message on the
// left, who reviews on the right (the cycle task's card, or a reviewer picker
// for a revision), and the diff below. Posting goes through the same submit
// verb the header popovers used before (SubmissionsController#create).
export default function Submit({
  slug,
  begin_date,
  end_date,
  top_down_plan,
  period,
  plan_period,
  actuals_months,
  plan_nodes,
  channel_section,
  quarterly_section,
  revision_diff,
  submit_task,
  people,
  revision_submit,
}: Props) {
  const market = useMarket()
  const [message, setMessage] = useState('')
  const [reviewerIds, setReviewerIds] = useState<string[]>(revision_submit?.default_reviewer_ids ?? [])
  const hasReviewers = submit_task ? submit_task.reviewers.length > 0 : reviewerIds.length > 0
  const firstPublish = revision_diff?.first_publish ?? false
  // Month labels for a revision's actual-vs-plan split; null for a plain
  // version, so every column that shows one carries its months.
  const periodLabels = useMemo(
    () => revisionLabels(plan_period, actuals_months, begin_date, period),
    [plan_period, actuals_months, begin_date, period]
  )
  // Full year = actual sales (top-level nodes only, to avoid double-counting
  // descendants) + this version's plan target.
  const fullYearTarget = useMemo(
    () => plan_nodes.reduce((sum, n) => sum + Number(n.actualRevenue ?? 0), 0) + top_down_plan.revenue_target,
    [plan_nodes, top_down_plan.revenue_target]
  )

  // Copy follows the outcome: with a reviewer the submit is reversible; without,
  // it publishes the plan (first submit) or updates the published plan (revision).
  // A revision's no-reviewer warning already sits on the Reviewers card.
  const hint = hasReviewers
    ? (firstPublish ? 'The plan is published once a reviewer approves.' : 'Published numbers stay as they are until a reviewer approves.')
    : (firstPublish ? 'No reviewer on this task — this will publish the plan right away.' : null)
  const submitLabel = hasReviewers ? 'Submit for review' : firstPublish ? 'Mark completed' : 'Publish changes'

  const submit = () => {
    const back_to = plan_index_path(market.slug)
    if (submit_task) {
      router.post(submissions_path(), { task_id: submit_task.id, message, back_to })
    } else if (revision_submit) {
      router.post(submissions_path(), { plan_id: revision_submit.plan_id, reviewer_ids: reviewerIds, message, back_to })
    }
  }

  return (
    <AppLayout
      header={<PlanHeader
        name={top_down_plan.name}
        orgNodeName={top_down_plan.org_node?.name ?? null}
        revenueTarget={top_down_plan.revenue_target}
        currency={top_down_plan.currency}
        beginDate={begin_date}
        endDate={end_date}
        planPeriod={plan_period}
        actualsMonths={actuals_months}
        fullYearTarget={periodLabels ? fullYearTarget : null}
      />}
    >
      <Head title={`${top_down_plan.name} — Submit`} />
      {/* Bottom margin here, not on the diff banner: a quarterly revision has
          no banner, and the sections below still need room. */}
      <div className="flex items-start gap-4 mb-6">
        <div className="min-w-0 lg:w-3/5">
          <SubmitMessageCard
            label={hasReviewers ? 'Message to your reviewer' : 'Message'}
            placeholder={firstPublish ? 'Anything your reviewer should know' : 'What changed and why'}
            hint={hint}
            warning={!hasReviewers}
            message={message}
            onChange={setMessage}
            submitLabel={submitLabel}
            onSubmit={submit}
            onCancel={() => router.visit(plan_top_down_path(market.slug, slug))}
          />
        </div>
        {submit_task
          ? <ReviewTaskCard task={submit_task} people={people} />
          : revision_submit && (
            <ReviewersCard people={revision_submit.people} selectedIds={reviewerIds} onChange={setReviewerIds} />
          )}
      </div>
      {revision_diff && (
        <RevisionBanner
          baseline={revision_diff}
          planNodes={plan_nodes}
          currency={top_down_plan.currency}
          legend={firstPublish
            ? 'Green rows are what will be published.'
            : 'Green is what this submission adds and red is the published value it replaces.'}
          className="mb-4"
        />
      )}
      <PlanBreakdownSections
        planNodes={plan_nodes}
        channelSection={channel_section}
        quarterlySection={quarterly_section}
        currency={top_down_plan.currency}
        periodLabels={periodLabels}
        baseline={revision_diff}
      />
    </AppLayout>
  )
}
